/**
 * CroOppsCreatedTile — CRO "Opps Created (trailing 90 days)" (2026-07-10 demo).
 *
 * Shows, for Product opps created in the last 90 days:
 *   - headline count + total $ created
 *   - the current-stage mix as a single stacked horizontal bar ("stage the opps
 *     are in now")
 *   - WoW + MoM pills for BOTH count and $, comparing the current 90-day window
 *     to the same window as it stood ~7 / ~30 days ago (field-history
 *     reconstructed, from GET /api/cro/opps-created).
 *
 * Reuses the CroArrPacingTile 4-state machine (loading / cache-warming /
 * SF-unreachable / success).
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../lib/api-client.js';
import { formatAmount, formatCompactCurrencyK, formatCountInt } from '../lib/format.js';

import { SfUnreachableBanner } from './SfUnreachableBanner.js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card.js';
import { Skeleton } from './ui/Skeleton.js';
import { Tooltip } from './ui/Tooltip.js';

import type { CroOppsCreatedResponse } from '@saledash/shared';

interface CroOppsCreatedTileProps {
  data: CroOppsCreatedResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

// Stage color palette — cool→warm across the pipeline, closed stages muted.
const STAGE_COLORS: Record<string, string> = {
  Discovery: '#93c5fd',
  Demo: '#60a5fa',
  Scoping: '#38bdf8',
  'Business Win': '#34d399',
  'Technical Win': '#22c55e',
  Confirmed: '#a3e635',
  Negotiation: '#fbbf24',
  'Closed Won': '#16a34a',
  'Closed Lost': '#cbd5e1',
};
const FALLBACK_COLORS = ['#818cf8', '#f472b6', '#fb923c', '#2dd4bf', '#c084fc'];

function colorForStage(stage: string, idx: number): string {
  return STAGE_COLORS[stage] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length] ?? '#818cf8';
}

/** Single-row stacked horizontal bar of the current-stage mix. */
function buildStageMixOption(buckets: { stage: string; count: number }[]): object {
  const series = buckets.map((b, i) => ({
    name: b.stage,
    type: 'bar',
    stack: 'mix',
    data: [b.count],
    itemStyle: { color: colorForStage(b.stage, i) },
    label: {
      show: b.count > 0,
      formatter: `{c}`,
      color: '#fff',
      fontSize: 10,
      fontWeight: 600,
    },
    emphasis: { focus: 'series' },
  }));

  return {
    animation: false,
    grid: { left: 8, right: 8, top: 30, bottom: 6, containLabel: false },
    legend: {
      show: true,
      top: 0,
      left: 0,
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 8,
      itemGap: 8,
      textStyle: { fontSize: 10, color: '#6b7280' },
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      formatter: (p: { seriesName: string; value: number }) =>
        `${p.seriesName}: <strong>${formatCountInt(p.value)}</strong>`,
    },
    xAxis: { type: 'value', show: false },
    yAxis: { type: 'category', data: [''], show: false },
    series,
  };
}

/** WoW/MoM delta chip. `format` renders the absolute delta (count or $). */
function DeltaPill({
  label,
  current,
  prior,
  format,
}: {
  label: string;
  current: number;
  prior: number;
  format: (v: number) => string;
}): React.JSX.Element {
  const diff = current - prior;
  const glyph = diff > 0 ? '▲' : diff < 0 ? '▼' : '—';
  const cls =
    diff > 0
      ? 'bg-good-bg text-good-text'
      : diff < 0
        ? 'bg-bad-bg text-bad-text'
        : 'bg-ink-100 text-ink-500';
  const amount = diff === 0 ? 'flat' : format(Math.abs(diff));
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      <span className="text-ink-500">{label}</span>
      <span aria-hidden>{glyph}</span>
      <span className="tabular-nums">{amount}</span>
    </span>
  );
}

const TITLE = 'Opps Created';

export function CroOppsCreatedTile({
  data,
  isLoading,
  isError,
  error,
  refetch,
}: CroOppsCreatedTileProps): React.JSX.Element {
  const tooltipContent = (
    <>
      <p className="mb-1 font-semibold text-ink-900">Opps Created — trailing 90 days</p>
      <p>
        New Product opportunities created in the last 90 days, by the stage
        they&apos;re in now, with total pipeline $ created. WoW/MoM compare this
        window to the same 90-day window as it stood 7 / 30 days ago
        (reconstructed from opportunity field history).
      </p>
    </>
  );

  const header = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        {TITLE}
        {data ? ` — Trailing ${String(data.window_days)} days` : ''}
      </CardTitle>
      <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
    </CardHeader>
  );

  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid="cro-tile-opps-created">
        {header}
        <CardContent>
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span aria-hidden className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white">⌛</span>
            <div className="flex-1">
              <p className="font-semibold">Opps-created tile warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">First trailing-90d refresh expected within ~7 minutes.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid="cro-tile-opps-created">
        {header}
        <CardContent><SfUnreachableBanner onRetry={refetch} /></CardContent>
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Card data-testid="cro-tile-opps-created">
        {header}
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-9 w-40" />
            <Skeleton className="h-[120px] w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="cro-tile-opps-created">
      {header}
      <CardContent>
        {/* Headline: count + $ created */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <p className="text-3xl font-bold tabular-nums text-ink-900" data-testid="kpi-value">
            {formatCountInt(data.total_count)}
          </p>
          <span className="text-sm text-ink-600">
            opps ·{' '}
            <span className="font-semibold tabular-nums text-ink-900">
              {formatAmount(data.total_amount)}
            </span>{' '}
            created
          </span>
        </div>

        {/* WoW/MoM pills — count row + amount row */}
        <div className="mt-2 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Count</span>
            <DeltaPill label="WoW" current={data.total_count} prior={data.prior_week.count} format={formatCountInt} />
            <DeltaPill label="MoM" current={data.total_count} prior={data.prior_month.count} format={formatCountInt} />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Amount</span>
            <DeltaPill label="WoW" current={data.total_amount} prior={data.prior_week.amount} format={formatCompactCurrencyK} />
            <DeltaPill label="MoM" current={data.total_amount} prior={data.prior_month.amount} format={formatCompactCurrencyK} />
          </div>
        </div>

        {/* Current-stage mix */}
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
          By current stage
        </p>
        <div className="mt-1 h-[104px] w-full" data-testid="stage-mix-chart">
          <ReactECharts
            option={buildStageMixOption(data.buckets)}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge
            lazyUpdate={false}
          />
        </div>
      </CardContent>
    </Card>
  );
}
